import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import Button from '../../../../../../shared/components/general/Button';
import PageError from '../../../../../../shared/components/general/PageError';
import PmsLookupContainer from '../../../../../../shared/hooks/PmsLookupContainer';
import { silver_two, color_shades_darker } from '../../../../../../shared/constants/colors';
import { PmsCacheCategoryModel } from '../../../../../../shared/models/organizations/PmsLookupModels';

// Clears some or all of the server's cached PMS data (offices, providers, procedure types, ...) for the selected
// configuration. Use it when something was just added in the PMS (like a new office) and doesn't show up here yet.
// The button and its status are separate so the status can sit on its own line without reflowing the header.
// Needs a PmsLookupContainer.Provider above it.
export const useClearPmsCache = (configName: string, onCleared?: (type?: string) => void) => {
    const pms = PmsLookupContainer.useContainer();
    const [isClearing, setIsClearing] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [errors, setErrors] = useState<string[]>([]);
    // The configuration selected right now, to spot a clear that finishes after the selection changed
    const currentConfigId = useRef(pms.configId);
    currentConfigId.current = pms.configId;

    // A result for one configuration shouldn't stay up under another
    useEffect(() => {
        setMessage(null);
        setErrors([]);
    }, [pms.configId]);

    // type is a category from the cache summary, or undefined for everything
    const clear = async (type: string | undefined, label: string) => {
        const what = type ? `cached ${label.toLowerCase()}` : 'all cached PMS data';
        const confirmed = window.confirm(
            `Clear ${what} for ${configName}?\n\n` +
            `It will be pulled from the PMS again on the next lookup or call. Nothing in the PMS is changed.`
        );
        if (!confirmed) return;

        const requestedFor = pms.configId;
        setIsClearing(true);
        setMessage(null);
        setErrors([]);
        const result = await pms.clearServerCache(type);
        setIsClearing(false);
        // Another configuration was picked while this ran: don't report on it there or reload its offices under it
        if (currentConfigId.current !== requestedFor) return;
        if (result.resultType === "Ok") {
            const count = result.data?.deletedCount ?? 0;
            const subject = type ? label : 'PMS data';
            setMessage(count === 0
                ? `Nothing was cached for ${subject.toLowerCase()}.`
                : `Cleared ${count} cached ${count === 1 ? 'entry' : 'entries'} (${subject.toLowerCase()}). It will be pulled fresh from the PMS.`);
            onCleared?.(type);
        } else {
            setErrors(result.errors?.length ? result.errors : ["Couldn't clear the cached PMS data."]);
        }
    };

    return { clear, isClearing, message, errors };
};

const formatAge = (iso?: string): string => {
    const time = iso ? new Date(iso).getTime() : NaN;
    if (isNaN(time)) return '';
    const minutes = Math.max(0, Math.round((Date.now() - time) / 60000));
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    const days = Math.round(hours / 24);
    return `${days} day${days === 1 ? '' : 's'} ago`;
};

// e.g. "13 cached · oldest 1 day ago"
const describe = (category: PmsCacheCategoryModel): string => {
    const age = formatAge(category.oldestCachedAt);
    const when = age ? ` · ${category.entryCount === 1 ? '' : 'oldest '}${age}` : '';
    return `${category.entryCount} cached${when}`;
};

interface ClearPmsCacheButtonProps {
    isClearing: boolean;
    onClear: (type: string | undefined, label: string) => void;
}

// "Clear PMS cache" with a menu of what's cached for the selected configuration, by category, plus everything
export const ClearPmsCacheButton: React.FC<ClearPmsCacheButtonProps> = ({ isClearing, onClear }) => {
    const pms = PmsLookupContainer.useContainer();
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    // Close when clicking anywhere else
    useEffect(() => {
        if (!isOpen) return;
        const onMouseDown = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', onMouseDown);
        return () => document.removeEventListener('mousedown', onMouseDown);
    }, [isOpen]);

    const toggle = () => {
        if (!isOpen) {
            // Only reads what's stored on the server; nothing is pulled from the PMS
            pms.loadCacheSummary();
        }
        setIsOpen(!isOpen);
    };

    const choose = (type: string | undefined, label: string) => {
        setIsOpen(false);
        onClear(type, label);
    };

    const summary = pms.cacheSummary;
    const categories = summary.data ?? [];
    const total = categories.reduce((sum, category) => sum + category.entryCount, 0);

    return (
        <Wrapper ref={wrapperRef}>
            <ClearButton
                themes={['white-small']}
                text={isClearing ? 'Clearing...' : 'Clear PMS cache ▾'}
                onClick={toggle}
                disabled={isClearing}
            />
            {isOpen && (
                <Menu role="menu" aria-label="Cached PMS data">
                    {summary.isLoading ? (
                        <MenuNote>Loading what's cached...</MenuNote>
                    ) : summary.errors.length > 0 ? (
                        <MenuNote>Couldn't read the cache: {summary.errors[0]}</MenuNote>
                    ) : categories.length === 0 ? (
                        <MenuNote>Nothing is cached for this configuration.</MenuNote>
                    ) : (
                        <>
                            {categories.map(category => (
                                <MenuItem key={category.type} role="menuitem" type="button" onClick={() => choose(category.type, category.label)}>
                                    <span>{category.label}</span>
                                    <MenuDetail>{describe(category)}</MenuDetail>
                                </MenuItem>
                            ))}
                            <MenuDivider />
                            <MenuItem role="menuitem" type="button" onClick={() => choose(undefined, 'Everything')}>
                                <span>Everything</span>
                                <MenuDetail>{total} cached</MenuDetail>
                            </MenuItem>
                        </>
                    )}
                </Menu>
            )}
        </Wrapper>
    );
};

export const ClearPmsCacheStatus: React.FC<{ message: string | null; errors: string[] }> = ({ message, errors }) => (
    <>
        {message && <StatusText role="status">{message}</StatusText>}
        {errors.length > 0 && <PageError errors={errors.map(error => `Couldn't clear the cache: ${error}`)} />}
    </>
);

const Wrapper = styled.div`
    position: relative;
`;

const ClearButton = styled(Button)`
    height: 34px;
    min-height: 34px;
    padding: 0 16px;
    margin: 0;
    font-size: 13px;
    white-space: nowrap;
`;

const Menu = styled.div`
    position: absolute;
    top: calc(100% + 4px);
    right: 0;
    z-index: 20;
    width: 320px;
    padding: 4px;
    background-color: #ffffff;
    border: 1px solid ${silver_two};
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const MenuItem = styled.button`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 8px 12px;
    border: none;
    border-radius: 8px;
    background-color: transparent;
    font-family: Muli;
    font-size: 13px;
    color: #2d3748;
    text-align: left;
    cursor: pointer;

    &:hover, &:focus {
        background-color: #f1f5f9;
        outline: none;
    }
`;

const MenuDetail = styled.span`
    font-size: 12px;
    color: ${color_shades_darker};
    white-space: nowrap;
`;

const MenuDivider = styled.div`
    height: 1px;
    margin: 4px 8px;
    background-color: ${silver_two};
`;

const MenuNote = styled.div`
    padding: 8px 12px;
    font-family: Muli;
    font-size: 13px;
    font-style: italic;
    color: ${color_shades_darker};
`;

const StatusText = styled.div`
    font-family: Muli;
    font-size: 12px;
    color: ${color_shades_darker};
    text-align: right;
    margin: -4px 0 8px;
`;

export default ClearPmsCacheButton;
